/**
 * Dialog "Salvar na minha biblioteca" — clona um template do catálogo
 * (template-intel) pra dentro de uma pasta/projeto da biblioteca própria.
 *
 * Escolhe projeto + pasta destino (carrega a árvore do projeto) + nome.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, BookmarkPlus } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/hooks/use-toast';
import {
    getProjects, getFolderTree, cloneTemplateToLibrary, createFolder,
} from '@/services/api';
import type { TemplateLibraryItem } from '@/types/template-library';
import { flattenFolders } from './FolderTree';

interface Props {
    template: TemplateLibraryItem | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function CloneToLibraryDialog({ template, open, onOpenChange }: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [projectId, setProjectId] = useState<number | null>(null);
    const [folderId, setFolderId] = useState<number | null>(null);
    const [name, setName] = useState('');

    useEffect(() => {
        if (open && template) {
            setName(template.title || `Template ${template.template_id}`);
            setProjectId(template.project_id ?? null);
            setFolderId(null);
        }
    }, [open, template]);

    const { data: projects = [] } = useQuery({
        queryKey: ['template-library-projects'],
        queryFn: getProjects,
        staleTime: 1000 * 60 * 5,
        enabled: open,
    });

    const { data: tree } = useQuery({
        queryKey: ['folder-tree', projectId],
        queryFn: () => getFolderTree(projectId),
        enabled: open,
        staleTime: 1000 * 15,
    });

    const flatFolders = flattenFolders(tree?.folders ?? []);

    const mutation = useMutation({
        mutationFn: () => cloneTemplateToLibrary({
            template_intel_id: template!.template_id,
            project_id: projectId,
            folder_id: folderId,
            name: name || undefined,
        }),
        onSuccess: () => {
            toast({ title: 'Salvo na biblioteca', description: 'O template foi clonado pra "Meus templates".' });
            queryClient.invalidateQueries({ queryKey: ['custom-templates'] });
            queryClient.invalidateQueries({ queryKey: ['folder-tree'] });
            onOpenChange(false);
        },
        onError: (e: any) => toast({
            title: 'Falha ao salvar',
            description: e?.response?.data?.detail || e?.message, variant: 'destructive',
        }),
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <BookmarkPlus className="w-5 h-5 text-violet-600" /> Salvar na minha biblioteca
                    </DialogTitle>
                    <DialogDescription>
                        Cria uma cópia editável deste modelo em "Meus templates".
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="clone-name">Nome</Label>
                        <Input
                            id="clone-name" value={name} maxLength={255}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label>Projeto</Label>
                        <SearchableSelect
                            value={projectId == null ? '' : String(projectId)}
                            onValueChange={(v) => { setProjectId(v ? Number(v) : null); setFolderId(null); }}
                            options={[
                                { value: '', label: 'Pessoal (sem projeto)' },
                                ...projects.map((p) => ({ value: String(p.id), label: p.name })),
                            ]}
                            placeholder="Selecione o projeto"
                            searchPlaceholder="Buscar projeto..."
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label>Pasta</Label>
                        <SearchableSelect
                            value={folderId == null ? '' : String(folderId)}
                            onValueChange={(v) => setFolderId(v ? Number(v) : null)}
                            options={[
                                { value: '', label: 'Raiz (sem pasta)' },
                                ...flatFolders.map((f) => ({
                                    value: String(f.id),
                                    label: `${'— '.repeat(f.depth)}${f.name}`,
                                })),
                            ]}
                            placeholder="Raiz (sem pasta)"
                            searchPlaceholder="Buscar ou criar pasta..."
                            createLabel="Criar pasta"
                            onCreate={async (name) => {
                                const r = await createFolder({ name, project_id: projectId, parent_id: null });
                                await queryClient.invalidateQueries({ queryKey: ['folder-tree', projectId] });
                                return { value: String(r.id ?? ''), label: name };
                            }}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
                        Cancelar
                    </Button>
                    <Button onClick={() => mutation.mutate()} disabled={!name.trim() || mutation.isPending}>
                        {mutation.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                        Salvar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
